import { useRef, useEffect, useState } from 'react';
import 'pannellum/build/pannellum.css';
import 'pannellum/build/pannellum.js';

interface PannellumViewerProps {
  imageUrl: string;
  className?: string;
  autoLoad?: boolean;
  autoRotate?: number;
}

export function PannellumViewer({
  imageUrl,
  className = '',
  autoLoad = true,
  autoRotate = -2,
}: PannellumViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<PannellumViewerInstance | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!containerRef.current || !imageUrl) return;

    // Destroy previous viewer if it exists
    if (viewerRef.current) {
      viewerRef.current.destroy();
      viewerRef.current = null;
    }

    setError(null);

    if (!window.pannellum) {
      setError('Pannellum library failed to load');
      return;
    }

    try {
      viewerRef.current = window.pannellum.viewer(containerRef.current, {
        type: 'equirectangular',
        panorama: imageUrl,
        autoLoad,
        autoRotate,
        pitch: 0,
        yaw: 0,
        hfov: 100,
        showControls: true,
        mouseZoom: true,
      });
    } catch (e) {
      console.error('Pannellum initialization error:', e);
      setError('Failed to initialize 360° viewer');
    }

    return () => {
      if (viewerRef.current) {
        viewerRef.current.destroy();
        viewerRef.current = null;
      }
    };
  }, [imageUrl, autoLoad, autoRotate]);

  if (error) {
    return (
      <div className={className} style={{ height: '100%', minHeight: 320, position: 'relative' }}>
        <div className="flex items-center justify-center h-full text-muted-foreground">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ height: '100%', minHeight: 320, position: 'relative' }}
    />
  );
}
